import { Transaction } from './transactionApi';

/** The three answers to "has anyone paid you back yet?" on the expense form. */
export type SettledChoice = 'none' | 'full' | 'partial';

type SettledView = Pick<Transaction, 'shares' | 'linkedSettlements' | 'paidByUserId' | 'userId'>;

const cents = (amount: number): number => Math.round(Number(amount) * 100);

/** What each participant paid back with the expense, by user id. */
export function paidBackByUser(transaction: Pick<Transaction, 'linkedSettlements'>): Record<string, number> {
  const paid: Record<string, number> = {};
  for (const entry of transaction.linkedSettlements ?? []) {
    paid[entry.fromUserId] = (paid[entry.fromUserId] ?? 0) + Number(entry.amount);
  }
  return paid;
}

/** The parts owed to the payer: every share but the payer's own. */
const othersParts = (transaction: SettledView) => {
  const payerId = transaction.paidByUserId ?? transaction.userId;
  return (transaction.shares ?? []).filter((share) => share.userId !== payerId);
};

/** Whether an existing expense was recorded as unpaid, fully or partly paid back. */
export function settledChoiceOf(transaction: SettledView): SettledChoice {
  const paid = paidBackByUser(transaction);
  const others = othersParts(transaction);
  if (others.length === 0 || Object.keys(paid).length === 0) {
    return 'none';
  }
  const all = others.every((share) => cents(paid[share.userId] ?? 0) >= cents(share.amount));
  return all ? 'full' : 'partial';
}

/** For the list: settled on the spot, partly paid on the spot, or nothing to say. */
export function settledSummary(
  transaction: SettledView,
): { kind: 'all' } | { kind: 'partial'; entries: Array<{ userId: string; amount: number }> } | null {
  const choice = settledChoiceOf(transaction);
  if (choice === 'none') {
    return null;
  }
  if (choice === 'full') {
    return { kind: 'all' };
  }
  const paid = paidBackByUser(transaction);
  return {
    kind: 'partial',
    entries: Object.entries(paid)
      .filter(([, amount]) => cents(amount) > 0)
      .map(([userId, amount]) => ({ userId, amount })),
  };
}

/** One equal part of `amount` among `count` people, rounded down to the cent. */
export function equalPart(amount: number, count: number): number {
  if (!Number.isFinite(amount) || amount <= 0 || count <= 0) {
    return 0;
  }
  return Math.floor((amount * 100) / count) / 100;
}
